import { useTranslation } from 'react-i18next';
import type { IModuleDefinition } from '@platform/core/types';
import { useLabel } from '@platform/hooks/useLabel';
import { isDesigned, mainRecordTypeOf } from '@platform/engine/menu/moduleSections';
import { moduleNamespace } from '@platform/i18n/moduleBundle';
import { findModuleDefinition } from '@platform/modularity/moduleDefinitionRegistry';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import type { IEntityRow } from './IEntityRow';
import { overviewEntitiesOf } from './overviewEntitiesOf';

/**
 * Спроєктовані рядки «Таблиць модуля» огляду: MOD_ENTITIES прототипу (`designEntities` головного типу — з визначення сервера, а доки сервер
 * його не віддає, з визначення пакета) з рядками пакета `entities.<тип>.designEntities.<i>`; без них — перші шість спроєктованих типів у
 * порядку визначення (без прихованих і підтаблиць дочірніх таблиць).
 */
export function useOverviewEntityRows(module: IModuleDefinition): IEntityRow[] {
  const { t } = useTranslation();
  const label = useLabel();
  const strings = useModuleStrings();
  const main = mainRecordTypeOf(module);
  if (!main) return [];
  const entities = main.designEntities ?? findModuleDefinition(module.module.key)?.recordTypes.find((r) => r.key === main.key)?.designEntities;
  if (entities) {
    const ns = moduleNamespace(module.module.key);
    return overviewEntitiesOf(module, entities).map(({ entity, index }) => {
      const at = `entities.${main.key}.designEntities.${index}`;
      const description = entity.description ? t(`${at}.description`, { ns, defaultValue: label(entity.description) }) : '';
      return { key: `e${index}`, name: t(`${at}.name`, { ns, defaultValue: label(entity.name) }), description };
    });
  }
  return module.recordTypes.filter((rt) => isDesigned(rt) && !rt.hidden && (!rt.parent || rt.parent === main.key)).slice(0, 6)
    .map((rt) => ({ key: rt.key, name: strings.entity(rt.key, 'singular', rt.singular ?? rt.name), description: strings.entity(rt.key, 'description', rt.description ?? null) }));
}
